import {
  $applyNodeReplacement,
  $isTextNode,
  DOMConversion,
  DOMConversionMap,
  DOMConversionOutput,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from 'lexical';

export class ExtendedTextNode extends TextNode {
  constructor(text: string, key?: NodeKey) {
    super(text, key); // key는 clone 시에만 전달
  }

  static getType(): string {
    return 'extended-text';
  }

  static clone(node: ExtendedTextNode): ExtendedTextNode {
    const cloned = new ExtendedTextNode(node.__text, node.__key);
    // TextNode 의 나머지 속성 복사. 누락 시 getWritable(=clone) 마다 style/format 이 유실 →
    // 중첩 span 붙여넣기에서 setStyle 이 누적 안 되고 마지막 것만 남음(형광펜 배경색 유실).
    cloned.__format = node.__format;
    cloned.__style = node.__style;
    cloned.__mode = node.__mode;
    cloned.__detail = node.__detail;
    return cloned;
  }

  static importDOM(): DOMConversionMap | null {
    const importers = TextNode.importDOM();
    return {
      ...importers,
      code: () => ({
        conversion: patchStyleConversion(importers?.code),
        priority: 1,
      }),
      em: () => ({
        conversion: patchStyleConversion(importers?.em),
        priority: 1,
      }),
      span: (domNode: HTMLElement) => {
        const el = domNode as HTMLSpanElement;

        if (el.dataset.lexicalBeautifulMentionData) {
          return null;
        }

        const original = importers?.span?.(domNode);
        if (!original) {
          return null;
        }

        return {
          conversion: patchStyleConversion(importers?.span),
          priority: 1,
        };
      },
      strong: () => ({
        conversion: patchStyleConversion(importers?.strong),
        priority: 1,
      }),
      s: () => ({
        conversion: patchStyleConversion(importers?.s),
        priority: 1,
      }),
      /*
       * 취소선(~~..~~) 은 GFM 규격상 <del> 로 렌더된다 — 우리 뷰어(react-markdown +
       * remark-gfm 인 Markdown.tsx)도, GitHub·AI 챗 UI 도 마찬가지다.
       * 그런데 Lexical 기본 importDOM 은 <s> 만 알고 <del>/<strike> 는 모르는 태그로
       * 흘려보내, 렌더된 마크다운을 복사해 붙여넣으면 글자만 남고 취소선이 사라진다.
       * (같은 문단의 <strong> 은 살아남아 "취소선만 안 먹는" 것처럼 보인다)
       * <s> 와 같은 결과가 되도록 strikethrough 포맷을 직접 얹는다.
       */
      del: () => ({
        conversion: patchStyleConversion(strikethroughImporter),
        priority: 1,
      }),
      strike: () => ({
        conversion: patchStyleConversion(strikethroughImporter),
        priority: 1,
      }),
      sub: () => ({
        conversion: patchStyleConversion(importers?.sub),
        priority: 1,
      }),
      sup: () => ({
        conversion: patchStyleConversion(importers?.sup),
        priority: 1,
      }),
    };
  }

  static importJSON(serializedNode: SerializedTextNode): TextNode {
    return TextNode.importJSON(serializedNode);
  }

  isSimpleText() {
    return this.__type === 'extended-text' && this.__mode === 0;
  }

  exportJSON(): SerializedTextNode {
    return {
      ...super.exportJSON(),
      type: 'extended-text',
      version: 1,
    };
  }
}

/*
 * <del>/<strike> → strikethrough 변환기.
 * Lexical 기본 변환기(convertTextFormatElement)는 노드의 태그명을 자기 표에서 찾아
 * 포맷을 정하므로 <s> 용 변환기를 그대로 재사용할 수 없다(<del> 은 표에 없어 무시된다).
 * 그래서 <s> 와 같은 형태 — 자기 노드는 만들지 않고(node: null) 자식 텍스트에만
 * 포맷을 얹는 forChild — 로 직접 만든다.
 */
const strikethroughImporter = (): DOMConversion => ({
  conversion: (): DOMConversionOutput => ({
    forChild: (lexicalNode) => {
      if ($isTextNode(lexicalNode) && !lexicalNode.hasFormat('strikethrough')) {
        lexicalNode.toggleFormat('strikethrough');
      }
      return lexicalNode;
    },
    node: null,
  }),
});

export function $createExtendedTextNode(text: string): ExtendedTextNode {
  return $applyNodeReplacement(new ExtendedTextNode(text));
}

export function $isExtendedTextNode(
  node: LexicalNode | null | undefined,
): node is ExtendedTextNode {
  return node instanceof ExtendedTextNode;
}

// 스타일 선언 문자열 병합. incoming(바깥 ancestor span)을 베이스로, existing(이미 적용된 안쪽 값)이 충돌 시 우선.
// 중첩 span 에서 바깥 span 이 안쪽 span 의 background-color/color 등을 덮어써 지우는 것을 방지.
function mergeStyles(existing: string, incoming: string): string {
  const parse = (styleText: string): Map<string, string> => {
    const map = new Map<string, string>();
    styleText.split(';').forEach((decl) => {
      const idx = decl.indexOf(':');
      if (idx === -1) return;
      const prop = decl.slice(0, idx).trim().toLowerCase();
      const value = decl.slice(idx + 1).trim();
      if (prop && value) map.set(prop, value);
    });
    return map;
  };
  const merged = parse(incoming);
  parse(existing).forEach((value, prop) => merged.set(prop, value));
  return Array.from(merged, ([prop, value]) => `${prop}: ${value}`).join('; ');
}

// font-weight 는 스타일로 남기면 툴바 굵게 토글(format)로 해제되지 않고 이어 쓰는 글자에도 번짐 → bold format 으로 변환
const INLINE_STYLE_PROPS = [
  'background-color',
  'color',
  'font-family',
  'font-size',
  'text-decoration',
];

// 이 노드와 조상 span/font 의 인라인 스타일을 모아 병합(안쪽=자식 우선).
// Lexical 이 중첩 span(node:null)의 forChild 를 최안쪽만 적용해, 바깥 span 의 background-color(형광펜)/
// font-family 가 텍스트에 도달 못 하고 유실됨 → 최안쪽 변환에서 조상 스타일까지 끌어와 보존한다.
function collectStyleChain(el: HTMLElement): HTMLElement[] {
  // 자기 자신은 태그 무관 항상 포함(strong/em/code/sub/sup 인라인 스타일 유실 방지),
  // 조상은 인라인 스타일 캐리어(span/font)만 거슬러 올라간다.
  const chain: HTMLElement[] = [el];
  let cur: HTMLElement | null = el.parentElement;
  while (cur && (cur.tagName === 'SPAN' || cur.tagName === 'FONT')) {
    chain.push(cur);
    cur = cur.parentElement;
  }
  return chain;
}

function collectInlineStyleWithAncestors(chain: HTMLElement[]): string {
  const acc = new Map<string, string>();
  // 바깥(조상)부터 채워 안쪽이 나중에 덮어써 우선하도록.
  for (let i = chain.length - 1; i >= 0; i -= 1) {
    INLINE_STYLE_PROPS.forEach((prop) => {
      const value = chain[i].style.getPropertyValue(prop);
      if (value) acc.set(prop, value);
    });
  }
  return Array.from(acc, ([prop, value]) => `${prop}: ${value}`).join('; ');
}

// 가장 안쪽에 선언된 font-weight 기준. Lexical 기본 변환은 700/bold 만 bold 로 인정해 600·800·900 은 누락됨
function isBoldFontWeight(chain: HTMLElement[]): boolean {
  const weight = chain
    .map((node) => node.style.fontWeight)
    .find((value) => value !== '');
  if (!weight) return false;
  if (weight === 'bold' || weight === 'bolder') return true;
  const numeric = Number(weight);
  return Number.isFinite(numeric) && numeric >= 600;
}

function patchStyleConversion(
  originalDOMConverter?: (node: HTMLElement) => DOMConversion | null,
): (node: HTMLElement) => DOMConversionOutput | null {
  return (node) => {
    const original = originalDOMConverter?.(node);
    if (!original) {
      return null;
    }
    const originalOutput = original.conversion(node);

    if (!originalOutput) {
      return originalOutput;
    }

    // 현재 span + 조상 span 의 스타일을 합쳐 수집(중첩 span 유실 방지, 위 함수 주석 참조).
    const chain = collectStyleChain(node);
    const style = collectInlineStyleWithAncestors(chain);
    const bold = isBoldFontWeight(chain);

    return {
      ...originalOutput,
      forChild: (lexicalNode, parent) => {
        const originalForChild = originalOutput?.forChild ?? ((x) => x);
        const result = originalForChild(lexicalNode, parent);
        if (!$isTextNode(result)) return result;
        if (bold && !result.hasFormat('bold')) {
          result.toggleFormat('bold');
        }
        if (style.length) {
          // 여러 span 의 forChild 가 적용될 수 있으니 기존 스타일과 병합(안쪽 우선).
          return result.setStyle(mergeStyles(result.getStyle(), style));
        }
        return result;
      },
    };
  };
}
